import React from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { validatePassword, strengthColors } from '../utils/passwordValidation';

/**
 * Dynamic Password Strength Indicator.
 * @param {string} password - Current password input value.
 * @param {object} policy - Policy configuration from backend.
 */
const PasswordStrengthIndicator = ({ password, policy }) => {
  const status = validatePassword(password, policy);

  if (!password) return null;

  const getStrengthColor = () => {
    return strengthColors[status.strength] || 'bg-gray-200';
  };

  return (
    <div className="mt-3 space-y-3 animate-fade-in p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Password Strength</span>
        <span className={`text-xs font-bold ${getStrengthColor().replace('bg-', 'text-')}`}>
          {status.strength}
        </span>
      </div>

      {/* Strength Bar */}
      <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ease-out ${getStrengthColor()}`}
          style={{ width: `${status.score}%` }}
        />
      </div>

      {/* Checklist */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
        {status.requirements.map((item, index) => (
          <div key={index} className="flex items-center gap-2 transition-all duration-300">
            {item.met ? (
              <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
            ) : (
              <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600 shrink-0" />
            )}
            <span className={`text-xs font-medium transition-colors ${item.met ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;
